import {
  Button,
  DatePicker,
  Form,
  FormProps,
  Input,
  Modal,
  Select,
  Spin,
} from "antd";
import { Calendar, FileText, MapPin, Truck, X } from "lucide-react";
import { FC, useEffect } from "react";
import { CustomizeRequiredMark } from "~/components/common/custom-required-mark";
import { useGetProjects } from "~/services/project";
import {
  useCreateShuttle,
  useGetShuttleById,
  useGetVesselsForDropdown,
  useUpdateShuttle,
  VESSEL_KEYS,
} from "~/services/vessel";
import { useShuttleStore } from "../store/shuttle.store";
import dayjs from "dayjs";
import { useQueryClient } from "@tanstack/react-query";
import { useNotification } from "~/hooks";

type Props = {};

export const ShuttleModal: FC<Props> = () => {
  const [form] = Form.useForm();
  const qc = useQueryClient();

  const { success, error } = useNotification();

  const { formState, filters, closeShuttleModal } = useShuttleStore();

  const { data: shuttle, isLoading: isLoadingShuttle } = useGetShuttleById(
    formState.shuttleId
  );

  const { data: projects } = useGetProjects();
  const { data: vessels } = useGetVesselsForDropdown();

  useEffect(() => {
    if (!shuttle) return;
    form.setFieldsValue({
      projectId: shuttle.projectId,
      destination: shuttle.destination,
      loadDate: shuttle.loadDate ? dayjs(shuttle.loadDate) : null,
      departureDate: shuttle.departureDate
        ? dayjs(shuttle.departureDate)
        : null,
      arrivalDate: shuttle.arrivalDate ? dayjs(shuttle.arrivalDate) : null,
      returnDate: shuttle.returnDate ? dayjs(shuttle.returnDate) : null,
      vesselIds: shuttle.vessels.map((vessel) => vessel.id),
      notes: shuttle.notes,
    });
  }, [shuttle, form]);

  const createMutation = useCreateShuttle();
  const updateMutation = useUpdateShuttle();

  const handleSubmit: FormProps["onFinish"] = (values) => {
    if (
      !values.loadDate ||
      !values.departureDate ||
      !values.arrivalDate ||
      !values.returnDate
    ) {
      error({
        message: "Missing required fields",
        description: "Please fill in all required date fields.",
      });
      return;
    }

    const formattedValues = {
      ...values,
      loadDate: values.loadDate.toISOString(),
      departureDate: values.departureDate.toISOString(),
      arrivalDate: values.arrivalDate.toISOString(),
      returnDate: values.returnDate.toISOString(),
    };

    const mutation = formState.shuttleId ? updateMutation : createMutation;
    const payload = formState.shuttleId
      ? { id: formState.shuttleId, ...formattedValues }
      : formattedValues;

    mutation.mutate(payload, {
      onSuccess: () => {
        success({
          message: formState.shuttleId
            ? "Shuttle updated successfully"
            : "Shuttle created successfully",
        });
        closeShuttleModal();
        qc.invalidateQueries({
          queryKey: VESSEL_KEYS.GET_SHUTTLES(filters),
        });
      },
      onError: (err) => {
        console.error(
          `Error ${formState.shuttleId ? "updating" : "creating"} shuttle:`,
          err
        );
        error({
          message: `Error ${
            formState.shuttleId ? "updating" : "creating"
          } shuttle`,
          description: err.message,
        });
      },
    });
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;
  const modalTitle = shuttle
    ? "Edit Shuttle " + shuttle.project?.name
    : "New Shuttle";

  return (
    <Modal
      width={"70%"}
      open={formState.open}
      onCancel={closeShuttleModal}
      footer={null}
      classNames={{
        body: "p-0",
        content: "p-0",
      }}
    >
      <Spin spinning={isLoadingShuttle}>
        <Form
          form={form}
          layout="vertical"
          size="large"
          requiredMark={CustomizeRequiredMark}
          className="space-y-6"
          onFinish={handleSubmit}
        >
          <div className="mx-auto bg-white rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                {modalTitle}
              </h3>
            </div>

            <div className="p-6">
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="col-span-2">
                    <Form.Item
                      name="projectId"
                      label="Project Name"
                      rules={[
                        { required: true, message: "Please select a project" },
                      ]}
                      className="mb-0"
                    >
                      <Select
                        options={projects?.items.map((project) => ({
                          label: project.name,
                          value: project.id,
                        }))}
                        prefix={
                          <FileText size={16} className="text-gray-400 mr-2" />
                        }
                        placeholder="Select Project"
                      />
                    </Form.Item>
                  </div>

                  <div className="col-span-2">
                    <Form.Item
                      name="destination"
                      label="Destination"
                      className="mb-0"
                    >
                      <Input
                        prefix={
                          <MapPin size={16} className="text-gray-400 mr-2" />
                        }
                        placeholder="Enter Destination"
                      />
                    </Form.Item>
                  </div>

                  <div>
                    <Form.Item
                      name="loadDate"
                      label="Load Date"
                      rules={[
                        {
                          required: true,
                          message: "Please select a load date",
                        },
                      ]}
                      className="mb-0"
                    >
                      <DatePicker
                        prefix={
                          <Calendar size={16} className="text-gray-400 mr-2" />
                        }
                        suffixIcon={null}
                        className="w-full"
                      />
                    </Form.Item>
                  </div>

                  <div>
                    <Form.Item
                      name="departureDate"
                      label="Departure Date"
                      rules={[
                        {
                          required: true,
                          message: "Please select a departure date",
                        },
                      ]}
                      className="mb-0"
                    >
                      <DatePicker
                        prefix={
                          <Calendar size={16} className="text-gray-400 mr-2" />
                        }
                        suffixIcon={null}
                        className="w-full"
                      />
                    </Form.Item>
                  </div>

                  <div>
                    <Form.Item
                      name="arrivalDate"
                      label="Arrival Date"
                      rules={[
                        {
                          required: true,
                          message: "Please select an arrival date",
                        },
                      ]}
                      className="mb-0"
                    >
                      <DatePicker
                        prefix={
                          <Calendar size={16} className="text-gray-400 mr-2" />
                        }
                        suffixIcon={null}
                        className="w-full"
                      />
                    </Form.Item>
                  </div>

                  <div>
                    <Form.Item
                      name="returnDate"
                      label="Return Date"
                      rules={[
                        {
                          required: true,
                          message: "Please select a return date",
                        },
                      ]}
                      className="mb-0"
                    >
                      <DatePicker
                        prefix={
                          <Calendar size={16} className="text-gray-400 mr-2" />
                        }
                        suffixIcon={null}
                        className="w-full"
                      />
                    </Form.Item>
                  </div>

                  <div className="col-span-2">
                    <Form.Item
                      name="vesselIds"
                      label="Vessels"
                      className="mb-0"
                    >
                      <Select
                        mode="multiple"
                        options={vessels?.map((vessel) => ({
                          label: (
                            <div className="flex items-center">
                              <Truck size={16} className="text-gray-400 mr-2" />
                              <div>
                                <div className="font-medium">{vessel.name}</div>
                                <div className="text-xs text-gray-500">
                                  {vessel.vesselType.name} •{" "}
                                  {vessel.licensePlate}
                                </div>
                              </div>
                            </div>
                          ),
                          value: vessel.id,
                        }))}
                        prefix={
                          <Truck size={16} className="text-gray-400 mr-2" />
                        }
                        className="w-full"
                        placeholder="Select Vessels"
                        maxTagCount={0}
                        maxTagPlaceholder={(omittedValues) =>
                          `${omittedValues.length} vessel(s) selected`
                        }
                        dropdownRender={(menu) => (
                          <div>
                            <div className="p-2 border-b border-gray-200 bg-gray-50 sticky top-0">
                              <div className="text-xs font-medium text-gray-500">
                                Select one or more vessels
                              </div>
                            </div>
                            {menu}
                          </div>
                        )}
                      />
                    </Form.Item>

                    <Form.Item dependencies={["vesselIds"]} noStyle>
                      {({ getFieldValue, setFieldValue }) => {
                        const selectedVesselIds = getFieldValue(
                          "vesselIds"
                        ) as string[];

                        const handleVesselToggle = (vesselId: string) => {
                          setFieldValue(
                            "vesselIds",
                            selectedVesselIds.filter((id) => id !== vesselId)
                          );
                        };

                        if (
                          !selectedVesselIds ||
                          selectedVesselIds.length === 0
                        )
                          return null;

                        return (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {selectedVesselIds.map((vesselId) => {
                              const vessel = vessels?.find(
                                (v) => v.id === vesselId
                              );
                              return vessel ? (
                                <div
                                  key={vesselId}
                                  className="inline-flex items-center bg-blue-50 border border-blue-100 rounded-md px-2 py-1"
                                >
                                  <span className="text-xs font-medium text-blue-700">
                                    {vessel.name}
                                  </span>
                                  <button
                                    type="button"
                                    className="ml-1 text-blue-400 hover:text-blue-600"
                                    onClick={() => handleVesselToggle(vesselId)}
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              ) : null;
                            })}
                          </div>
                        );
                      }}
                    </Form.Item>
                  </div>

                  <div className="col-span-2">
                    <Form.Item name="notes" label="Notes (Optional)">
                      <Input.TextArea rows={3} />
                    </Form.Item>
                  </div>
                </div>

                <div className="flex justify-end space-x-4 pt-4 border-t">
                  <Button
                    htmlType="button"
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    onClick={closeShuttleModal}
                  >
                    Cancel
                  </Button>
                  <Button
                    htmlType="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    loading={isLoading}
                  >
                    {formState.shuttleId ? "Edit Shuttle" : "Create Shuttle"}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Schedule Summary */}
          <div className="mx-auto mt-6 bg-white rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Shuttle Schedule Summary
              </h3>
            </div>
            <Form.Item shouldUpdate noStyle>
              {({ getFieldValue }) => {
                const projectId = getFieldValue("projectId");
                const destination = getFieldValue("destination");
                const loadDate = getFieldValue("loadDate");
                const departureDate = getFieldValue("departureDate");
                const arrivalDate = getFieldValue("arrivalDate");
                const returnDate = getFieldValue("returnDate");
                const selectedVesselIds =
                  (getFieldValue("vesselIds") as string[]) || [];
                const project = projects?.items.find((p) => p.id === projectId);

                if (
                  !project ||
                  !destination ||
                  !loadDate ||
                  !departureDate ||
                  !arrivalDate ||
                  !returnDate
                ) {
                  return (
                    <div className="p-8 text-center text-gray-500">
                      Please fill in all required fields to see schedule summary
                    </div>
                  );
                }

                return (
                  <div className="p-6">
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <Calendar className="h-5 w-5 text-blue-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <h4 className="text-lg font-medium text-gray-900">
                            {project.name}
                          </h4>
                          <p className="text-gray-500">
                            <MapPin className="inline-block h-4 w-4 mr-1" />
                            {destination}
                          </p>
                        </div>
                      </div>

                      <div className="border-l-2 border-blue-200 pl-8 ml-5 space-y-6">
                        {loadDate && (
                          <div className="relative">
                            <div className="absolute -left-10 mt-1.5">
                              <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center border-2 border-white">
                                <div className="w-2 h-2 rounded-full bg-green-600"></div>
                              </div>
                            </div>
                            <p className="text-sm font-medium">
                              {loadDate.format("MMMM D, YYYY")}
                            </p>
                            <p className="text-sm text-gray-500">Load Day</p>
                          </div>
                        )}

                        {departureDate && (
                          <div className="relative">
                            <div className="absolute -left-10 mt-1.5">
                              <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center border-2 border-white">
                                <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                              </div>
                            </div>
                            <p className="text-sm font-medium">
                              {departureDate.format("MMMM D, YYYY")}
                            </p>
                            <p className="text-sm text-gray-500">Departure</p>
                          </div>
                        )}

                        {arrivalDate && (
                          <div className="relative">
                            <div className="absolute -left-10 mt-1.5">
                              <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center border-2 border-white">
                                <div className="w-2 h-2 rounded-full bg-purple-600"></div>
                              </div>
                            </div>
                            <p className="text-sm font-medium">
                              {arrivalDate.format("MMMM D, YYYY")}
                            </p>
                            <p className="text-sm text-gray-500">Arrival</p>
                          </div>
                        )}

                        {returnDate && (
                          <div className="relative">
                            <div className="absolute -left-10 mt-1.5">
                              <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center border-2 border-white">
                                <div className="w-2 h-2 rounded-full bg-red-600"></div>
                              </div>
                            </div>
                            <p className="text-sm font-medium">
                              {returnDate.format("MMMM D, YYYY")}
                            </p>
                            <p className="text-sm text-gray-500">Return</p>
                          </div>
                        )}
                      </div>

                      {selectedVesselIds.length > 0 && (
                        <div className="mt-6">
                          <h5 className="text-sm font-medium text-gray-700 mb-2">
                            Assigned Vessels
                          </h5>
                          <div className="flex flex-col space-y-2">
                            {selectedVesselIds.map((vesselId) => {
                              const vessel = vessels?.find(
                                (v) => v.id === vesselId
                              );
                              return vessel ? (
                                <div
                                  key={vesselId}
                                  className="flex items-center p-2 bg-gray-50 rounded-md"
                                >
                                  <Truck
                                    size={16}
                                    className="text-gray-400 mr-2"
                                  />
                                  <div>
                                    <span className="font-medium">
                                      {vessel.name}
                                    </span>
                                    <span className="text-xs text-gray-500 ml-2">
                                      {vessel.vesselType.name} •{" "}
                                      {vessel.licensePlate}
                                    </span>
                                  </div>
                                </div>
                              ) : null;
                            })}
                          </div>
                        </div>
                      )}

                      <div className="mt-4 grid grid-cols-2 gap-4">
                        {selectedVesselIds.length > 0 ? (
                          selectedVesselIds.map((vesselId) => {
                            const vessel = vessels?.find(
                              (v) => v.id === vesselId
                            );

                            if (!vessel) return null;

                            // Calculate vessel volume (total capacity)
                            const vesselVolume =
                              (vessel.boxLength ?? 0) *
                              (vessel.internalHeight ?? 0) *
                              (vessel.doorWidth ?? 0);

                            // Calculate total inventory volume assigned to this vessel
                            const totalInventoryVolume =
                              vessel.serviceTickets?.reduce((acc, ticket) => {
                                // Sum up the volume of all inventories in the ticket
                                const ticketVolume = (
                                  ticket.inventories || []
                                ).reduce((invAcc, inventory) => {
                                  // Use inventory dimensions if available
                                  const length = inventory.length ?? 0;
                                  const width = inventory.width ?? 0;
                                  const height = inventory.height ?? 0;

                                  const inventoryVolume =
                                    length > 0 && width > 0 && height > 0
                                      ? length * width * height
                                      : 0;

                                  return invAcc + inventoryVolume;
                                }, 0);

                                return acc + ticketVolume;
                              }, 0) ?? 0;

                            // Calculate capacity percentage
                            // Prevent division by zero
                            const capacityPercentage =
                              vesselVolume > 0
                                ? Math.min(
                                    Math.round(
                                      (totalInventoryVolume / vesselVolume) *
                                        100
                                    ),
                                    100
                                  )
                                : 0;

                            // Determine color based on capacity percentage
                            const capacityColorClass =
                              capacityPercentage > 90
                                ? "text-red-600"
                                : capacityPercentage > 75
                                ? "text-amber-600"
                                : "text-green-600";

                            return (
                              <div
                                key={vessel.id}
                                className="bg-white p-3 border rounded-lg"
                              >
                                <div className="flex items-center mb-2">
                                  <Truck
                                    size={16}
                                    className="text-gray-500 mr-2"
                                  />
                                  <h6 className="font-medium text-sm">
                                    {vessel.name} Load Summary
                                  </h6>
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  <div>
                                    <span className="text-gray-500">
                                      Bookings:
                                    </span>
                                    <span className="ml-2 font-medium">
                                      {vessel.serviceTickets?.length ?? 0}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">
                                      Packages:
                                    </span>
                                    <span className="ml-2 font-medium">
                                      {vessel.serviceTickets?.reduce(
                                        (acc, ticket) =>
                                          acc +
                                          (ticket.inventories?.length ?? 0),
                                        0
                                      ) ?? 0}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">
                                      Volume:
                                    </span>
                                    <span className="ml-2 font-medium">
                                      {vesselVolume.toFixed(1)} ft³
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">
                                      Used Volume:
                                    </span>
                                    <span className="ml-2 font-medium">
                                      {totalInventoryVolume.toFixed(1)} ft³
                                    </span>
                                  </div>
                                  <div className="col-span-2">
                                    <div className="flex items-center mt-1">
                                      <span className="text-gray-500 mr-2">
                                        Capacity Used:
                                      </span>
                                      <div className="flex-1">
                                        <div className="bg-gray-200 h-2 rounded-full w-full">
                                          <div
                                            className={`h-2 rounded-full ${
                                              capacityPercentage > 90
                                                ? "bg-red-600"
                                                : capacityPercentage > 75
                                                ? "bg-amber-600"
                                                : "bg-green-600"
                                            }`}
                                            style={{
                                              width: `${capacityPercentage}%`,
                                            }}
                                          ></div>
                                        </div>
                                      </div>
                                      <span
                                        className={`ml-2 font-medium ${capacityColorClass}`}
                                      >
                                        {capacityPercentage}%
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <h5 className="text-sm font-medium text-gray-700 mb-2 col-span-2">
                            No load summary available
                          </h5>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }}
            </Form.Item>
          </div>
        </Form>
      </Spin>
    </Modal>
  );
};
